import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLoggedInUser, useFeatureFlags } from '@tcc/shared-data-lib';
import { useHistory, useLocation } from 'react-router-dom';
import lift from '@tcc/load-lifter';
import { getMessage } from '@tcc/i18n';
import { FdsIconType } from '@tcc/fds';
import { importFromS3 } from '../../utils/importFromS3';
import { useUserLoggedInConfigs, useFeatureFlagsConfigs } from '../../utils/sharedData';
import { useNavigationLinks } from '../../hooks/useNavigationLinks/useNavigationLinks';
import { useNavFooterItems } from '../../hooks/useNavFooterItems';
import { mergeNavigationItems, MergedNavItem } from '../../utils/mergeNavigationItems';
import { Operation } from '../../hooks/useOperations/types';
import { shouldShowOperationSelector } from '../../utils/shouldShowOperationSelector';
import { shouldShowMultiOperationSelector } from '../../utils/shouldShowMultiOperationSelector';
import { toggleOperationSelectionMode } from '../../utils/toggleOperationSelectionMode';
import { getOperationSelectorDisplayText } from '../../utils/getOperationSelectorDisplayText';
import { HermesLibrary } from '../../types/Hermes';
import { useUserSubMenu } from '../../hooks/useUserSubMenu';

type UserInfoProps = {
  email: string;
  fullName: string;
  avatar: string;
};

type SubMenuItemProps = {
  label: string;
  leadingIcon?: FdsIconType;
  dataTestId?: string;
  onClick: () => void;
};

type FooterItem = {
  label: string;
  href?: string;
  icon?: React.ReactElement;
  testId?: string;
  target?: string;
  onClick?: () => void;
};

// TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
type NavLink = {
  icon?: React.ReactElement;
  label: string;
  href?: string;
  testId?: string;
  target?: string;
  isActive?: boolean;
};

// TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
type NavMenu = {
  id: string,
  links: NavLink[];
};

type Props = {
  allOperationsDisplayMessage: string
  areNotificationsLoading: boolean;
  expanded: boolean;
  hasMultiOperationEnabled: boolean;
  hasOperationDropdown: boolean;
  // TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
  menuLinks: NavLink[];
  // TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
  menues: NavMenu[];
  // TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
  support?: NavLink;
  // TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
  expandCollapseButton: NavLink;
  navigationItems?: MergedNavItem[];
  navFooterItems?: FooterItem[];
  onLogoClick?: () => void;
  operations: Operation[];
  operationsSelected: Operation[];
  operationsSelectedDisplayMessage: string;
  setExpandedState: (isExpanded: boolean) => void;
  setSelectedOperations: (operationSelected: Operation[]) => void;
  subMenusUserProfile: SubMenuItemProps[];
  userInfo: UserInfoProps;
};

interface IProps {
  expanded: boolean;
  operations: Operation[];
  operationsSelected: Operation[];
  setExpandedState: (isExpanded: boolean) => void;
  setSelectedOperations: (operationSelected: Operation[]) => void;
}

const InformationArchitecturePlugin: React.FC<Props> = React.lazy(() =>
  importFromS3('information-architecture')
);

export const InfoArchitecturePluginContainer: React.FC<IProps> = ({
  expanded,
  operations,
  operationsSelected,
  setExpandedState,
  setSelectedOperations,
}: IProps) => {
  const { links, menues } = useNavigationLinks();
  const { user, user: { details } } = useLoggedInUser(useUserLoggedInConfigs);
  const { featureFlags } = useFeatureFlags(useFeatureFlagsConfigs(user));
  const [areNotificationsLoading, setNotificationsLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();


  useEffect(() => {
    if (user && operationsSelected.length > 0) {
      lift(['tcc-hermes'], (Hermes: HermesLibrary) => {
        setNotificationsLoading(false);
        Hermes.install(
          null,
          {
            containerSelector: '#hermesButton',
            insideDashboard: false,
          },
          user.details,
          operationsSelected[0].operationId
        );
      });
    }
  }, [user, operationsSelected]);

  const userInfo = {
    fullName: `${details?.firstName} ${details?.lastName}`,
    email: details?.email,
    avatar: `${details?.firstName?.charAt(0)}${details?.lastName?.charAt(0)}`,
  };

  const hasOperationDropdown = shouldShowOperationSelector({ user, route: location.pathname }) && operations.length !== 0;

  const hasMultiOperationEnabled = shouldShowMultiOperationSelector(location.pathname, featureFlags);

  toggleOperationSelectionMode({
    hasMultiOperationEnabled,
    operationsSelected,
    operations,
    setSelectedOperations
  });

  const operationsSelectedDisplayMessage = getOperationSelectorDisplayText({
    operations,
    operationsSelected
  });

  const navigationItems = useMemo(() => 
    mergeNavigationItems(links, menues), 
  [links, menues]
  );

  const subMenus = useUserSubMenu({ featureFlags, user });

  const navFooterItems = useNavFooterItems({
    expanded,
    setExpandedState,
    countryCode: details?.countryCode,
    featureFlags,
  });

  const onLogoClick = useCallback(() => {
    history.push(`/${location.search}`);
  }, [history, location]);

  return (
    <InformationArchitecturePlugin
      allOperationsDisplayMessage={getMessage('sidenav.allOperations')}
      areNotificationsLoading={areNotificationsLoading}
      expanded={expanded}
      hasOperationDropdown={hasOperationDropdown}
      hasMultiOperationEnabled={hasMultiOperationEnabled}
      // TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
      menuLinks={links}
      // TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
      menues={menues}
      navigationItems={navigationItems}
      navFooterItems={navFooterItems}
      // TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
      support={navFooterItems[0]}
      // TODO REMOVE AFTER FEWEB-2183 IS COMPLETE
      expandCollapseButton={navFooterItems[navFooterItems.length - 1]}
      onLogoClick={onLogoClick}
      operations={operations}
      operationsSelected={operationsSelected}
      operationsSelectedDisplayMessage={operationsSelectedDisplayMessage}
      setExpandedState={setExpandedState}
      setSelectedOperations={setSelectedOperations}
      subMenusUserProfile={subMenus}
      userInfo={userInfo}
    />
  );
};
